// Region helpers: canonical lowercase region params, selectable jurisdictions, default and last region, and the active region from the route context.
import { useRouterState } from "@tanstack/react-router";

import type { JurisdictionOut } from "@/api/__generated__";
import { readStorage, STORAGE_KEYS, writeStorage } from "@/lib/storage";

export interface RegionContextValue {
  jurisdiction: JurisdictionOut;
  currentDocUid: string | null;
}

export function regionParam(jurisdiction: Pick<JurisdictionOut, "jurisdiction_id">): string {
  return jurisdiction.jurisdiction_id.toLowerCase();
}

export function selectableRegions(jurisdictions: readonly JurisdictionOut[]): JurisdictionOut[] {
  return jurisdictions.filter((jurisdiction) => jurisdiction.selectable);
}

export function findRegion(
  jurisdictions: readonly JurisdictionOut[],
  param: string,
): JurisdictionOut | undefined {
  const wanted = param.toLowerCase();
  return jurisdictions.find((jurisdiction) => jurisdiction.jurisdiction_id.toLowerCase() === wanted);
}

export function readLastRegion(): string | null {
  return readStorage(STORAGE_KEYS.lastRegion);
}

export function writeLastRegion(param: string): void {
  writeStorage(STORAGE_KEYS.lastRegion, param.toLowerCase());
}

export function defaultRegion(
  jurisdictions: readonly JurisdictionOut[],
  lastRegion: string | null = readLastRegion(),
): JurisdictionOut | undefined {
  const selectable = selectableRegions(jurisdictions);
  const remembered = lastRegion === null ? undefined : findRegion(selectable, lastRegion);
  return remembered ?? selectable[0];
}

export function regionSummary(jurisdiction: JurisdictionOut): string {
  const parts = [jurisdiction.current_family_title, jurisdiction.current_version_label].filter(
    (part): part is string => typeof part === "string" && part.length > 0,
  );
  return parts.join(" · ");
}

export function useActiveRegion(): RegionContextValue | null {
  return useRouterState({
    select: (state) => {
      const match = state.matches.find((candidate) => candidate.routeId === "/_app/$region");
      const context = match?.context as { region?: RegionContextValue } | undefined;
      return context?.region ?? null;
    },
  });
}

export function useRegion(): RegionContextValue {
  const region = useActiveRegion();
  if (region === null) {
    throw new Error("useRegion must be used inside a /$region route.");
  }
  return region;
}

export function useRegionParam(explicit?: string): string | null {
  const region = useActiveRegion();
  if (explicit !== undefined) {
    return explicit;
  }
  return region === null ? null : regionParam(region.jurisdiction);
}
